/**
 * RefreshWaveFlow — Iter271 P1 (COACHDIA Pulse)
 * ==============================================
 *
 * Flow dédié "Nouvelle vague complète" : toute l'équipe re-passe le test.
 *
 *   [Confirm] → POST /teams/{id}/waves wave_type=refresh
 *       ↓  (retour : wave + invitations envoyées à TOUT le core_roster)
 *   [Tracking multi-membres] → polling GET /teams/{id} toutes 10s
 *       ↓  (progress.completed >= seuil min)
 *   [Generate] → POST /batches/{waveId}/generate-report
 *       ↓
 *   [Done]
 *
 * Garanties :
 *  ❌ N'écrase pas le rapport précédent (report_unlocked stricte guard)
 *  ✅ Anciens rapports restent accessibles dans Section Rapports (v1, v2, vN)
 *  ✅ Génère un rapport "vN+1 · Nouvelle vague (équipe complète)"
 *  ✅ Nouveaux membres optionnels (ajouter quelqu'un en même temps que la refresh)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, RefreshCw, Mail, Clock, Check, Loader2, ArrowRight, RotateCw,
  FileText, Plus, Trash2,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEP = { CONFIRM: 'confirm', TRACKING: 'tracking', GENERATING: 'generating', DONE: 'done' };

export default function RefreshWaveFlow({ open, onClose, teamId, teamName, coreRoster = [], onReportGenerated }) {
  const [step, setStep] = useState(STEP.CONFIRM);
  const [addNew, setAddNew] = useState([]);   // Optional newcomers [{name, email}]
  const [loading, setLoading] = useState(false);
  const [waveId, setWaveId] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0, in_progress: 0 });
  const [pollError, setPollError] = useState(null);

  const reset = () => {
    setStep(STEP.CONFIRM); setAddNew([]); setLoading(false);
    setWaveId(null); setProgress({ completed: 0, total: 0, in_progress: 0 }); setPollError(null);
  };
  const closeAll = () => { reset(); onClose(); };

  /* ---------- Step 1 → 2 : création wave refresh ---------- */
  const submitWave = async () => {
    setLoading(true);
    try {
      const payload = {
        wave_type: 'refresh',
        new_participants: addNew
          .filter((p) => p.email && p.email.includes('@'))
          .map((p) => ({ email: p.email.trim().toLowerCase(), full_name: (p.name || '').trim() })),
        batch_name: `${teamName} — Nouvelle vague complète`,
        validity_days: 7,
      };
      const r = await axios.post(
        `${API}/team-pulse/teams/${teamId}/waves`, payload, { headers: authHeaders() },
      );
      setWaveId(r.data.wave?.id);
      setStep(STEP.TRACKING);
      toast.success(`Invitations envoyées à ${r.data.wave?.invited_emails?.length || 0} membre(s)`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Impossible de lancer la nouvelle vague.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step 2 : polling ---------- */
  const pollStatus = useCallback(async () => {
    if (!waveId) return;
    try {
      const r = await axios.get(`${API}/team-pulse/teams/${teamId}`, { headers: authHeaders() });
      const waves = r.data.waves || [];
      const cw = waves.find((w) => w.batch.id === waveId);
      if (!cw) return;
      setProgress(cw.progress || { completed: 0, total: 0, in_progress: 0 });
      setPollError(null);
    } catch (e) {
      setPollError(e.response?.data?.detail || 'Erreur de synchronisation');
    }
  }, [waveId, teamId]);

  useEffect(() => {
    if (step !== STEP.TRACKING || !waveId) return;
    pollStatus();
    const iv = setInterval(pollStatus, 10000);
    return () => clearInterval(iv);
  }, [step, waveId, pollStatus]);

  /* ---------- Step 2 → 3 : génération ---------- */
  const generateReport = async () => {
    if (!waveId) return;
    setStep(STEP.GENERATING);
    setLoading(true);
    try {
      await axios.post(
        `${API}/team-pulse/batches/${waveId}/generate-report`, {},
        { headers: authHeaders() },
      );
      toast.success('Nouveau rapport généré');
      setStep(STEP.DONE);
      if (onReportGenerated) onReportGenerated();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Génération impossible.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur inconnue.');
      setStep(STEP.TRACKING);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const canGenerate = progress.completed >= 2;  // seuil Pulse

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="pulse-refresh-wave-modal">
      <div className="bg-[#F5F1EA] max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl relative">
        {/* Header */}
        <div className="sticky top-0 bg-[#1F1A12] text-white px-8 py-5 flex items-start justify-between rounded-t-2xl z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#F3D060] uppercase font-semibold">
              COACHDIA · PULSE · Nouvelle vague
            </div>
            <h2 className="font-serif text-2xl mt-1">Nouvelle vague complète</h2>
            <div className="text-[#EEE0BE] text-sm mt-0.5 italic font-light">
              {teamName} · Toute l'équipe re-passe le test
            </div>
          </div>
          <button onClick={closeAll} className="text-white/70 hover:text-white transition" data-testid="pulse-refresh-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8">
          {step === STEP.CONFIRM && (
            <ConfirmScreen
              coreRoster={coreRoster}
              addNew={addNew} setAddNew={setAddNew}
              loading={loading} onSubmit={submitWave}
            />
          )}
          {step === STEP.TRACKING && (
            <TrackingScreen
              progress={progress} pollError={pollError}
              canGenerate={canGenerate} onGenerate={generateReport}
            />
          )}
          {step === STEP.GENERATING && (
            <div className="py-16 text-center" data-testid="pulse-refresh-generating">
              <Loader2 className="w-10 h-10 animate-spin text-[#92710A] mx-auto mb-4" />
              <div className="font-serif text-xl text-[#1F1A12]">Génération du nouveau rapport...</div>
              <p className="text-sm text-[#6B5E48] mt-2 max-w-md mx-auto">
                Mesure complète de l'équipe sur les tests réels de cette vague.
              </p>
            </div>
          )}
          {step === STEP.DONE && (
            <div className="py-8 text-center" data-testid="pulse-refresh-done">
              <div className="w-14 h-14 rounded-full bg-[#5B8A6B] text-white flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7" />
              </div>
              <h3 className="font-serif text-2xl text-[#1F1A12] mb-2">
                Nouveau rapport disponible.
              </h3>
              <p className="text-sm text-[#6B5E48] max-w-md mx-auto mb-6 leading-relaxed">
                La mesure complète de l'équipe a été effectuée. Les rapports précédents
                restent intacts pour comparaison historique.
              </p>
              <button
                onClick={closeAll}
                className="bg-[#1F1A12] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2A2319] transition inline-flex items-center gap-2"
                data-testid="pulse-refresh-open-report"
              >
                Voir le dashboard mis à jour <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Sub-screens
 * ========================================================================== */

const ConfirmScreen = ({ coreRoster, addNew, setAddNew, loading, onSubmit }) => {
  const addEntry = () => setAddNew([...addNew, { name: '', email: '' }]);
  const removeEntry = (i) => setAddNew(addNew.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, value) => {
    const next = [...addNew];
    next[i] = { ...next[i], [field]: value };
    setAddNew(next);
  };

  return (
    <div className="space-y-5" data-testid="pulse-refresh-confirm">
      <div className="bg-[#F3D060]/10 border border-[#F3D060]/40 rounded-lg p-4 text-sm text-[#1F1A12] leading-relaxed">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-[#92710A] shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Ce qui va se passer</div>
            <ul className="mt-2 space-y-1 text-[#6B5E48] text-[13px]">
              <li>• Tous les membres actuels du roster recevront un <span className="font-semibold text-[#1F1A12]">nouveau lien de test</span> à compléter.</li>
              <li>• Les <span className="font-semibold text-[#1F1A12]">anciens rapports restent intacts</span> — accessibles dans la section Rapports.</li>
              <li>• Un nouveau rapport sera généré dès que le seuil minimum (2 complétions) est atteint.</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#92710A] font-semibold mb-3">
          Membres du roster — {coreRoster.length} personne{coreRoster.length > 1 ? 's' : ''}
        </div>
        <div className="bg-white border border-[#E5DFD0] rounded-xl divide-y divide-[#E5DFD0] max-h-44 overflow-y-auto">
          {coreRoster.length === 0 ? (
            <div className="p-4 text-sm text-[#6B5E48] italic">Aucun membre dans le roster.</div>
          ) : coreRoster.map((email) => (
            <div key={email} className="px-4 py-3 flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-[#92710A] shrink-0" />
              <span className="text-[#1F1A12] truncate">{email}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bonus : ajouter de nouveaux membres en même temps */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#92710A] font-semibold">
            Ajouter de nouveaux membres <span className="text-[#8A7A5E] normal-case tracking-normal">— optionnel</span>
          </div>
          <button
            onClick={addEntry}
            className="text-[11px] uppercase tracking-wider text-[#92710A] font-semibold hover:underline flex items-center gap-1"
            data-testid="pulse-refresh-add-new"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
        {addNew.length > 0 && (
          <div className="space-y-2">
            {addNew.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input
                  value={p.name} onChange={(e) => updateEntry(i, 'name', e.target.value)}
                  placeholder="Nom complet"
                  className="px-3 py-2 border border-[#6B5E48]/30 rounded-lg bg-white text-sm focus:border-[#92710A] focus:outline-none"
                  data-testid={`pulse-refresh-new-name-${i}`}
                />
                <input
                  type="email" value={p.email} onChange={(e) => updateEntry(i, 'email', e.target.value)}
                  placeholder="email@entreprise.com"
                  className="px-3 py-2 border border-[#6B5E48]/30 rounded-lg bg-white text-sm focus:border-[#92710A] focus:outline-none"
                  data-testid={`pulse-refresh-new-email-${i}`}
                />
                <button
                  onClick={() => removeEntry(i)}
                  className="px-3 text-[#B94A48] hover:bg-[#F5E0DE] rounded-lg transition"
                  data-testid={`pulse-refresh-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={onSubmit} disabled={loading}
        className="w-full bg-[#1F1A12] text-white py-3.5 rounded-full font-medium hover:bg-[#2A2319] transition flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="pulse-refresh-submit"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi des invitations...</>
          : <><Mail className="w-4 h-4" /> Lancer la nouvelle vague</>}
      </button>
    </div>
  );
};

const TrackingScreen = ({ progress, pollError, canGenerate, onGenerate }) => {
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  return (
    <div className="space-y-5" data-testid="pulse-refresh-tracking">
      <div className="bg-white border border-[#E5DFD0] rounded-xl p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold">
            Progression collective
          </div>
          <div className="tabular-nums text-sm font-medium text-[#1F1A12]">
            {progress.completed} / {progress.total}
          </div>
        </div>
        <div className="h-3 bg-[#E5DFD0] rounded-full overflow-hidden">
          <div className="h-full bg-[#5B8A6B] transition-[width] duration-700"
               style={{ width: `${pct}%` }} data-testid="pulse-refresh-progress-bar" />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B5E48] font-semibold">Complétés</div>
            <div className="font-serif text-2xl tabular-nums text-[#5B8A6B]">{progress.completed}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B5E48] font-semibold">En cours</div>
            <div className="font-serif text-2xl tabular-nums text-[#C8A64B]">{progress.in_progress || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B5E48] font-semibold">En attente</div>
            <div className="font-serif text-2xl tabular-nums text-[#8A7A5E]">
              {Math.max(0, (progress.total || 0) - (progress.completed || 0) - (progress.in_progress || 0))}
            </div>
          </div>
        </div>
      </div>

      {pollError && (
        <div className="text-xs text-[#B94A48] italic">{pollError}</div>
      )}

      <div className="bg-[#FAF5EA] border border-[#E5DFD0] rounded-xl p-4 text-sm text-[#6B5E48] leading-relaxed flex items-start gap-3">
        <RotateCw className="w-4 h-4 text-[#92710A] shrink-0 mt-0.5" />
        <div>
          Synchronisation automatique toutes les 10 secondes. Vous pouvez fermer cette
          fenêtre et revenir plus tard — la vague reste active jusqu'à expiration (7 jours).
        </div>
      </div>

      <button
        onClick={onGenerate} disabled={!canGenerate}
        className="w-full bg-[#1F1A12] text-white py-3 rounded-full font-medium hover:bg-[#2A2319] transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="pulse-refresh-generate-report"
      >
        {canGenerate ? (
          <><FileText className="w-4 h-4" /> Générer le nouveau rapport</>
        ) : (
          <><Clock className="w-4 h-4" /> En attente (minimum 2 complétions requises)</>
        )}
      </button>
    </div>
  );
};
